import React, { useState } from 'react';
import {
  isClinicModuleEnabled,
  isSimpleShippingEnabled,
  usePharmacyFeatures,
} from '../../hooks/queries/usePharmacyFeatures';
import * as Sentry from '@sentry/react';
import { BRAND } from '../../branding';
import { usePharmacyBranding } from '../../hooks/usePharmacyBranding';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Badge,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Receipt as BillingIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalServicesIcon,
  Chat as ChatIcon,
  Assignment as OrderIcon,
  Assignment as AssignmentIcon,
  ExpandLess,
  ExpandMore,
  CalendarToday as CalendarIcon,
  Description,
  QrCodeScanner,
  VerifiedUser,
  Science,
  MenuBook,
  BugReport,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  History as HistoryIcon,
  LocalShipping,
  MedicationOutlined,
  Queue as QueueIcon,
  Map as WorkflowIcon,
  AdminPanelSettings as AdminIcon,
  DriveEta as DriverIcon,
  PersonOff as PersonOffIcon,
  ListAlt,
  FactCheck as FactCheckIcon,
  SupportAgent as SupportAgentIcon,
  Assessment as AssessmentIcon,
  GridView as BlisterPackIcon,
  Tune as DeliverySetupIcon,
  Public as TerritoryIcon,
  LocalHospital as PrescribersIcon,
  Person as DoctorIcon,
  Business as ClinicIcon,
  MoveToInbox as PhoxEntryQueueIcon,
  Vaccines as PspQueueIcon,
  AccessTime as TimeClockIcon,
} from '@mui/icons-material';
import { REPORT_ACCESS_PERMISSIONS } from '../../constants/reportCatalog';
import { CALENDAR_VISIBILITY_KEY } from '../../constants/appointments';
import { DELIVERY_TRACKING_ROLES } from '../../constants/deliveryTracking';
import { useBlocksHidden } from '../../hooks/queries/usePharmacyVisibility';
import { TESTING_CENTER_ROLES, isTestingCenterEnabled } from '../../testing-center/environment';
import { isInDevelopmentNavEnabled } from '../../utils/environmentUtils';
import { NURSE_BLOCKED_SECTIONS, NURSE_LANDING_PATH, isNurseBlockedPath } from '../../constants/nurseScope';
import { useNurseScope } from '../../hooks/useNurseScope';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { useAuth } from '../../hooks/useAuth';
import { useUnreadConversationBadge } from '../../hooks/queries/useUnreadConversationBadge';
import { useTaskQueueCounts } from '../../hooks/queries/useTaskQueueCounts';
import { ReleaseNotesModal } from '../common/ReleaseNotesModal';
import { shortcutForPath } from '../../constants/keyboardShortcuts';

/**
 * Live counts the nav can show next to an item (GP-1400). Give a menu item a
 * `badgeKey` and feed the number in `dynamicBadges` below — that's the whole
 * contract, so the next queue that wants one (Task Queue, Order Troubleshooting)
 * adds a key here rather than a bespoke badge.
 */
type NavBadgeKey = 'unreadConversations' | 'myOpenTasks';

/** Screen-reader wording for each live badge (the number alone says nothing). */
const NAV_BADGE_LABELS: Record<NavBadgeKey, string> = {
  unreadConversations: 'unread conversations',
  myOpenTasks: 'open tasks assigned to me',
};

interface SubMenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  badge?: number | 'new' | 'IP';
  /** Live count fed from a shell-level query; wins over the static `badge`. */
  badgeKey?: NavBadgeKey;
  permission?: string;
  /** Same role allow-list as `MenuItem.roles` (PRX-126: Delivery territories). */
  roles?: string[];
  /**
   * PRX-24 / PRX-16: same module tag as `MenuItem`, so a sub-item (Ready to
   * Ship, Staging, Shipping all live under Main Operations) can be gated too.
   */
  module?: 'clinic' | 'shipping';
  /** PRX-16: same inverse tag as `MenuItem` — see the field there for why. */
  hideWhenModule?: 'shipping';
  /** PRX-143: a PRX-54 visibility block key; the sub-item drops when the pharmacy hides it. */
  visibilityKey?: string;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: SubMenuItem[];
  badge?: number | 'new' | 'IP';
  /** Live count fed from a shell-level query; wins over the static `badge`. */
  badgeKey?: NavBadgeKey;
  journeyColor?: string;
  isSection?: boolean;
  isDivider?: boolean;
  isCollapsibleSection?: boolean;
  permission?: string;
  /** Any ONE of these permissions reveals the item (OR semantics, GP-1327). */
  permissions?: string[];
  roles?: string[];
  /** When `false`, hides the item regardless of role/permission (fail-closed env gate). */
  envEnabled?: boolean;
  /** Keep the item highlighted on nested routes (`/reports/delivery-method`). */
  matchNestedPaths?: boolean;
  /**
   * PRX-24 / PRX-16: the pharmacy-level module this entry belongs to. Every
   * entry of a module — section header included — drops out of the nav when the
   * pharmacy does not have that module.
   */
  module?: 'clinic' | 'shipping';
  /**
   * PRX-16: the inverse. This entry drops out when the pharmacy DOES have the
   * module, because the module replaced it. Only `shipping` uses it: the one
   * Shipping page stands in for Ready to Ship and Staging, and leaving all
   * three reachable would let a bag be closed on one screen and signed out on
   * another.
   */
  hideWhenModule?: 'shipping';
  /**
   * PRX-126: a PRX-54 visibility block key (visibility/visibilityCatalogue.ts).
   * On a section header it drops the whole section, header and entries, when
   * the pharmacy hid that block (System Configuration -> Visibility).
   * PRX-104: on a standalone entry it drops that entry.
   * PRX-134: on an entry with sub-items it drops the entry and its sub-items.
   * PRX-143: on a sub-item it drops that sub-item only.
   */
  visibilityKey?: string;
}

/**
 * PRX-24 / PRX-16 — whether the pharmacy's modules allow this entry. Exported
 * and pure so both rules are tested without rendering a nav.
 *
 * Two flags with OPPOSITE defaults, so they are read separately rather than
 * folded into one loop: `clinic` is on unless a pharmacy switched it off, and
 * `shipping` is off unless a pharmacy switched it on.
 */
export function menuItemAllowedByModules(
  item: Pick<MenuItem, 'module' | 'hideWhenModule'>,
  modules: { clinicModuleEnabled: boolean; simpleShippingEnabled: boolean },
): boolean {
  if (item.module === 'clinic' && !modules.clinicModuleEnabled) return false;
  if (item.module === 'shipping' && !modules.simpleShippingEnabled) return false;
  if (item.hideWhenModule === 'shipping' && modules.simpleShippingEnabled) return false;
  return true;
}

/** Exported for `constants/keyboardShortcuts.spec.ts`, which fails if a GP-482
 *  hotkey ever points at a path/permission this menu no longer has. */
export const getMenuItems = (pharmacyId: string | null): MenuItem[] => [
    { text: 'Operations Command Center', icon: <DashboardIcon />, path: '/order-fulfillment/operations-dashboard' },
  // Main Operations drilldown
  {
    text: 'Main Operations',
    icon: <WorkflowIcon />,
    subItems: [
      { text: 'PHOX Entry', icon: <AssignmentIcon />, path: '/order-fulfillment/Phox-entry', permission: 'viewPatients' },
      // PRX-143: requests pushed from the Communication Center, waiting to be
      // entered. Hidden by default except on a Fillware pharmacy (the visibility catalogue).
      { text: 'PHOX Entry queue', icon: <PhoxEntryQueueIcon />, path: '/order-fulfillment/phox-entry-queue', permission: 'viewPatients', visibilityKey: 'sidebar.item.phox_entry_queue' },
      { text: 'Billing Queue', icon: <BillingIcon />, path: '/order-fulfillment/billing-queue', permission: 'viewBilling' },
      { text: 'Scheduling Queue', icon: <ScheduleIcon />, path: '/order-fulfillment/schedule-delivery', permission: 'viewDeliveries' },
      { text: 'Clinical Verification', icon: <Science />, path: '/order-fulfillment/clinical-verification', permission: 'viewClinicalData' },
      { text: 'Packaging', icon: <InventoryIcon />, path: '/order-fulfillment/packaging', permission: 'viewInventory' },
      { text: 'Product Verification', icon: <VerifiedUser />, path: '/order-fulfillment/product-verification', permission: 'viewInventory' },
      { text: 'Ready to Ship', icon: <LocalShipping />, path: '/order-fulfillment/ready-to-ship', permission: 'viewDeliveries', hideWhenModule: 'shipping' },
      { text: 'Staging', icon: <QueueIcon />, path: '/order-fulfillment/staging-area', permission: 'viewDeliveries', hideWhenModule: 'shipping' },
      // PRX-16: one page in place of the two above, for a pharmacy with no
      // drivers of its own. Same permission — it is the same work.
      { module: 'shipping', text: 'Shipping', icon: <LocalShipping />, path: '/order-fulfillment/shipping', permission: 'viewDeliveries' },
      { text: 'Delivery Status', icon: <CheckCircle />, path: '/order-fulfillment/delivery-status', permission: 'viewDeliveries' },
      { text: 'All Orders', icon: <ListAlt />, path: '/order-fulfillment/all-orders', permission: 'viewPatients' },
    ]
  },

  // Standalone Items
  { text: 'Subscription Management', icon: <AssignmentIcon />, path: '/order-fulfillment/subscription-management', permission: 'viewBilling' },
  // PRX-104: the pharmacy-wide Blister pack page. Its Billing list needs
  // viewBilling and its SynMed list viewPatients, so either grants the entry.
  // Hidden by default except on a Fillware pharmacy (the visibility catalogue).
  { text: 'Blister pack', icon: <BlisterPackIcon />, path: '/blister-pack', permissions: ['viewBilling', 'viewPatients'], visibilityKey: 'sidebar.item.blister_pack' },
  // PRX-129: the PSP queue (specialty drug files and prior authorization).
  // Rafik's PharmAssist workflow, so hidden by default except on a Fillware
  // pharmacy (the visibility catalogue).
  { text: 'PSP queue', icon: <PspQueueIcon />, path: '/psp-queue', permission: 'viewPatients', visibilityKey: 'sidebar.item.psp_queue' },
  // PRX-134: the Doctors directory and Prescriber clinics (not the IV/delivery
  // clinic module). Fillware prescribers, so hidden by default except on a
  // Fillware pharmacy (the visibility catalogue).
  {
    text: 'Prescribers',
    icon: <PrescribersIcon />,
    visibilityKey: 'sidebar.item.prescribers',
    subItems: [
      { text: 'Doctors', icon: <DoctorIcon />, path: '/prescribers/doctors', permission: 'viewPatients' },
      { text: 'Prescriber clinics', icon: <ClinicIcon />, path: '/prescribers/clinics', permission: 'viewPatients' },
    ],
  },
  // PRX-140: the time clock. Every member of the pharmacy clocks in, so no
  // permission gates the entry; the manager tabs inside need manageUsers.
  // Hidden by default except on a Fillware pharmacy (the visibility catalogue).
  { text: 'Time clock', icon: <TimeClockIcon />, path: '/time-clock', visibilityKey: 'sidebar.item.time_clock' },
  // GP-1400: the unread count now rides along here, so staff see replies are
  // waiting without opening /communications first.
  { text: 'Communication Center', icon: <ChatIcon />, path: '/communications', badgeKey: 'unreadConversations' },
  { text: 'Faxes', icon: <Description />, path: '/kroll-faxes' },
  // PRX-67: badge shows how many open tasks are assigned to the signed-in
  // user, mirroring the Communication Center's unread count above.
  { text: 'Task Queue', icon: <QueueIcon />, path: '/tasks', badgeKey: 'myOpenTasks' },
  // PRX-139: appointments and MedsCheck booking. Read on viewPatients (the
  // route's own key). Hidden by default except on a Fillware pharmacy (the
  // visibility catalogue).
  { text: 'Calendar', icon: <CalendarIcon />, path: '/calendar', permission: 'viewPatients', visibilityKey: CALENDAR_VISIBILITY_KEY },
  { text: 'Reimbursement Tracker', icon: <AssignmentIcon />, path: '/crm/reimbursements', roles: ['wally_dev_admin', 'pharmacy_admin', 'onepoint', 'onepoint_manager'] },
  // Reports (GP-1327): previously reachable ONLY from the avatar dropdown, and
  // only for admins/data analysts — so a Logistics user granted the Delivery
  // Method Report had no way to find it. Visible to anyone holding any report
  // grant; the hub itself decides which cards they see.
  { text: 'Reports', icon: <AssessmentIcon />, path: '/reports', permissions: REPORT_ACCESS_PERMISSIONS, matchNestedPaths: true },
  // PRX-126: couriers and delivery territories in one plain place. Territory
  // Management was reachable only from the avatar menu (Delivery tracking) and
  // Couriers sat inside the collapsed SETTINGS section. Navigation only: both
  // routes are unchanged, and Settings -> Couriers below stays because the
  // courier copy names that path (PRX-56). Each page keeps its own gate: the
  // parent shows when at least one of the two is visible.
  {
    text: 'Delivery setup',
    icon: <DeliverySetupIcon />,
    subItems: [
      { text: 'Couriers', icon: <LocalShipping />, path: '/admin/settings/couriers', permission: 'manageSettings' },
      { text: 'Delivery territories', icon: <TerritoryIcon />, path: '/delivery/territories', roles: DELIVERY_TRACKING_ROLES },
    ],
  },
  // Testing Center: gated by the VITE_TESTING_CENTER_ENABLED env flag AND the
  // Wally dev role (GP-1178). Env flag off → hidden for everyone (fail-closed);
  // when on, role-restricted items still show for users with NO custom role
  // (canSee's graceful fallback).
  { text: 'Testing Center', icon: <FactCheckIcon />, path: '/testing-center', roles: TESTING_CENTER_ROLES, envEnabled: isTestingCenterEnabled() },
  // { text: 'RC Embeddable', icon: <ChatIcon />, path: '/communications/embeddable', badge: 'new' },

  // Settings Section (PRX-56, R24). The Couriers page's own copy — the wizard's
  // "Set a default in Settings → Couriers", the backend's refusal sentence, the
  // page intro — tells staff to go to Settings → Couriers, and that path has to
  // exist. This sidebar had no Settings grouping (the tabbed /admin/settings
  // page is reached from the avatar menu and the Administration hub), so the
  // entry sat top-level between Task Queue and Reimbursement Tracker and the
  // three sentences named a route nobody could follow. Gated on the same
  // `manageSettings` key as every other pharmacy-admin setting.
  { text: 'SETTINGS', isSection: true, isCollapsibleSection: true, icon: <></>, permission: 'manageSettings' },
  { text: 'Couriers', icon: <LocalShipping />, path: '/admin/settings/couriers', permission: 'manageSettings' },
  // PRX-141: the chronic medication list behind blister eligibility. Global
  // reference data, same `manageSettings` gate as the routes behind it.
  { text: 'Chronic medication list', icon: <MedicationOutlined />, path: '/admin/settings/chronic-medications', permission: 'manageSettings' },

  // Quality Management Section (GP-207): a Collapse/Expand tab for the quality
  // forms the teams fill in outside PHOX today. Left un-permissioned so any
  // staff member can record a patient complaint (graceful fallback also shows
  // it when no role is loaded).
  { text: 'QUALITY MANAGEMENT', isSection: true, isCollapsibleSection: true, icon: <></> },
  { text: 'Complaint Form Data', icon: <SupportAgentIcon />, path: '/quality-management/complaint-forms' },

  // Clinic Management Section
  { module: 'clinic', text: 'CLINIC MANAGEMENT', isSection: true, isCollapsibleSection: true, icon: <></>, permission: 'editClinicalData' },
//   { text: 'Delivery Calendar', icon: <CalendarIcon />, path: '/clinic-delivery-calendar' },
// { text: 'NEXI Visits Planning', icon: <CalendarIcon />, path: '/nexi/visits-planning' },
  { module: 'clinic', text: 'IV Billing Queue', icon: <BillingIcon />, path: '/nexi/iv-billing' },
//   { text: 'Clinic Calendar View', icon: <CalendarIcon />, path: '/clinic-calendar' },
  // { text: 'Processed Orders', icon: <CheckCircle />, path: '/processed-orders' },
//   { text: 'NEXI Visit Queue', icon: <MedicalServicesIcon />, path: '/nexi/visit-queue' },

  { module: 'clinic', text: 'Ad-hoc Delivery Dates', icon: <ScheduleIcon />, path: '/clinic-ad-hoc-dates' },
  { module: 'clinic', text: 'Cancellation Alerts', icon: <MedicalServicesIcon />, path: '/nexi/cancellation-alerts' },
  { module: 'clinic', text: 'Excluded Patients', icon: <PersonOffIcon />, path: '/iv/excluded-patients' },
//   { text: 'Unlinked Patients', icon: <PersonOffIcon />, path: '/iv/unlinked-patients' },
  { module: 'clinic', text: 'NEXI Webhooks', icon: <HistoryIcon />, path: '/nexi/webhooks' },
  { module: 'clinic', text: 'IV Compliance', icon: <AssignmentIcon />, path: '/clinic-management/iv-compliance' },

  // Driver Management Section
  // PRX-126: a pharmacy with no drivers of its own (PharmAssist ships through
  // couriers) can hide this whole section. Shown unless an admin hides it.
  { text: 'DRIVER MANAGEMENT', isSection: true, isCollapsibleSection: true, icon: <></>, permission: 'viewDeliveries', visibilityKey: 'sidebar.section.driver_management' },
  { text: 'Driver Management', icon: <AdminIcon />, path: '/driver-management' },
  { text: 'Bag Scan Kiosk', icon: <QrCodeScanner />, path: pharmacyId ? `/kiosk?pharmacy=${pharmacyId}&mode=bag-first` : '/kiosk?mode=bag-first' },
  { text: 'Driver Select Kiosk', icon: <DriverIcon />, path: pharmacyId ? `/kiosk?pharmacy=${pharmacyId}&mode=driver-first` : '/kiosk?mode=driver-first' },
  { text: 'Bag History', icon: <InventoryIcon />, path: '/bag-history' },
  { text: 'Checkout History', icon: <HistoryIcon />, path: '/driver-checkout-history' },

  // In Development Section — gated behind the fail-closed
  // VITE_IN_DEVELOPMENT_NAV_ENABLED flag (GP-1196) so unfinished features
  // (Document Processing, Prescription Intake, Create Patient) never leak into
  // the production/UAT operational view. Flip the flag on to work on them
  // locally. The section's leading divider lives inside the gate so no dangling
  // separator is left behind when the group is hidden.
  ...(isInDevelopmentNavEnabled()
    ? ([
        { text: '', isDivider: true, icon: <></> },
        { text: 'IN DEVELOPMENT', isSection: true, isCollapsibleSection: true, icon: <></>, permission: 'manageSettings' },
        { text: 'Document Processing', icon: <Description />, path: '/data-intake' },
        { text: 'Prescription Intake', icon: <Science />, path: '/data-intake/batch', badge: 'new' },
        { text: 'Create Patient', icon: <PersonOffIcon />, path: '/data-intake/create-patient', badge: 'new' },
      ] as MenuItem[])
    : []),

];

/**
 * GP-482 — the queue's hotkey, shown right on the nav row. Quiet enough not to
 * compete with the label, present enough that staff learn the key without ever
 * opening the cheat sheet.
 */
const ShortcutHint: React.FC<{ combo: string }> = ({ combo }) => (
  <Typography
    variant="caption"
    sx={{
      ml: 1,
      px: 0.75,
      py: 0.1,
      flexShrink: 0,
      fontFamily: 'monospace',
      fontSize: '0.65rem',
      lineHeight: 1.6,
      color: 'text.disabled',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 0.75,
      whiteSpace: 'nowrap',
    }}
  >
    {combo.replace('Shift + ', '⇧')}
  </Typography>
);

/**
 * The menu this user may see: every sidebar rule applied once (permission,
 * roles, env flag, nurse scope, pharmacy modules, PRX-54 visibility keys).
 * PRX-138: exported so the keyboard shortcuts read the SAME visible set as the
 * nav. A queue the nav hides has no shortcut, with no second copy of the rules.
 */
export function useVisibleMenuItems(): MenuItem[] {
  const { pharmacyId } = usePharmacyStore();
  const { hasCustomPermission, hasCustomRole, customRole } = useAuth();
  const { isNurse } = useNurseScope();

  // Get menu items with dynamic pharmacy ID
  const rawMenuItems = React.useMemo(() => getMenuItems(pharmacyId), [pharmacyId]);

  // PRX-24: module visibility is a pharmacy-level setting (System Configuration
  // → Pharmacy Order Routing & modules), never a code change. Entries tagged
  // with a module leave the nav — section header included — when it is off.
  const { data: pharmacyFeaturesData } = usePharmacyFeatures(pharmacyId);
  const clinicModuleEnabled = isClinicModuleEnabled(pharmacyFeaturesData?.features);
  // PRX-16: off unless the pharmacy turned it on, so an absent flag and a
  // failed request both leave Ready to Ship and Staging exactly where they are.
  const simpleShippingEnabled = isSimpleShippingEnabled(pharmacyFeaturesData?.features);

  // PRX-126: sections tagged with a PRX-54 visibility key, resolved off one
  // visibility query. While it loads, each key resolves to its catalogue
  // default (shown), so nothing flickers away on a pharmacy that never set it.
  const visibilityKeys = React.useMemo(
    () =>
      rawMenuItems
        .flatMap((item) => [item.visibilityKey, ...(item.subItems ?? []).map((sub) => sub.visibilityKey)])
        .filter((k): k is string => !!k),
    [rawMenuItems],
  );
  const hiddenBlocks = useBlocksHidden(visibilityKeys);

  // Permission check helper: no role assigned = show everything (graceful fallback)
  const canSee = React.useCallback((item: Pick<MenuItem, 'permission' | 'permissions' | 'path' | 'roles' | 'envEnabled'>) => {
    const { permission, permissions, path, roles, envEnabled } = item;
    // Env-flag gate (fail-closed): an explicit `false` hides the item for
    // everyone, overriding the graceful role fallback below (GP-1178).
    if (envEnabled === false) return false;
    // Nurse scope (GP-1183): the nurse keeps Delivery Status, Communication
    // Center and Clinic Management only. The coarse permission flags can't
    // express that (Delivery Status and Ready to Ship share `viewDeliveries`),
    // so the nurse's blocked-path list — the same list the router guard uses —
    // filters the nav.
    if (isNurse && path && isNurseBlockedPath(path)) return false;
    if (!customRole) return true; // No role loaded = show everything (current behavior)
    // Role-restricted items: only visible to explicitly listed roles
    if (roles && roles.length > 0) {
      return roles.some(r => hasCustomRole(r));
    }
    // OR-list of permissions: any one grants the item (GP-1327 — the Reports
    // entry is reachable via `viewReports` OR a single per-report grant).
    if (permissions && permissions.length > 0) {
      return permissions.some(p => hasCustomPermission(p));
    }
    if (!permission) return true; // No permission required = always visible
    // Role-based sidebar overrides (items visible beyond their default permission)
    return hasCustomPermission(permission);
  }, [customRole, hasCustomPermission, hasCustomRole, isNurse]);

  // Filter menu items based on permissions
  return React.useMemo(() => {
    const filtered: MenuItem[] = [];
    let currentSectionHidden = false;

    for (let i = 0; i < rawMenuItems.length; i++) {
      const item = rawMenuItems[i];

      // PRX-24 / PRX-16: this pharmacy's modules decide whether the entry exists.
      if (!menuItemAllowedByModules(item, { clinicModuleEnabled, simpleShippingEnabled })) continue;

      // Section headers: if section has a permission, check it.
      // Nurses additionally lose whole sections by name (DRIVER MANAGEMENT),
      // which no permission flag can express — they hold `viewDeliveries`.
      if (item.isSection) {
        const nurseBlockedSection = isNurse && NURSE_BLOCKED_SECTIONS.includes(item.text);
        const hiddenByPharmacy = !!item.visibilityKey && hiddenBlocks[item.visibilityKey] === true;
        currentSectionHidden =
          nurseBlockedSection || hiddenByPharmacy || !canSee({ permission: item.permission });
        if (!currentSectionHidden) {
          filtered.push(item);
        }
        continue;
      }

      // Dividers: show if not in a hidden section
      if (item.isDivider) {
        if (!currentSectionHidden) {
          filtered.push(item);
        }
        continue;
      }

      // Items under a hidden section: skip
      // Check if this item belongs to the current section by looking backwards
      const belongsToSection = (() => {
        for (let j = i - 1; j >= 0; j--) {
          if (rawMenuItems[j].isSection) return true;
          if (rawMenuItems[j].isDivider) return false;
          if (!rawMenuItems[j].isSection && !rawMenuItems[j].isDivider && rawMenuItems[j].subItems) return false;
        }
        return false;
      })();

      if (belongsToSection && currentSectionHidden) {
        continue;
      }

      // Items with subItems: filter children individually, hide parent if no children remain
      if (item.subItems) {
        // PRX-134: a parent the pharmacy hid (or hides by default) goes with its children.
        if (item.visibilityKey && hiddenBlocks[item.visibilityKey] === true) continue;
        const visibleSubItems = item.subItems.filter(
          (sub) =>
            menuItemAllowedByModules(sub, { clinicModuleEnabled, simpleShippingEnabled }) &&
            !(sub.visibilityKey && hiddenBlocks[sub.visibilityKey] === true) &&
            canSee({ permission: sub.permission, path: sub.path, roles: sub.roles }),
        );
        if (visibleSubItems.length === 0) continue;
        filtered.push({ ...item, subItems: visibleSubItems });
        continue;
      }

      // PRX-104: a standalone entry the pharmacy hid (or hides by default).
      if (item.visibilityKey && hiddenBlocks[item.visibilityKey] === true) continue;

      // Standalone items with their own permission
      if (!canSee(item)) continue;

      filtered.push(item);
    }

    return filtered;
  }, [rawMenuItems, canSee, clinicModuleEnabled, simpleShippingEnabled, hiddenBlocks]);
}

/** PRX-138: every route in a (filtered) menu, top-level entries and sub-items alike. */
export function visibleMenuPaths(items: Pick<MenuItem, 'path' | 'subItems'>[]): Set<string> {
  const paths = new Set<string>();
  for (const item of items) {
    if (item.path) paths.add(item.path);
    for (const sub of item.subItems ?? []) paths.add(sub.path);
  }
  return paths;
}

interface NewSidebarProps {
  open: boolean;
  onClose?: () => void;
  variant: 'permanent' | 'persistent' | 'temporary';
  width: number;
  collapsed?: boolean;
  onCollapseToggle?: () => void;
}

export const NewSidebar: React.FC<NewSidebarProps> = ({
  open,
  onClose,
  variant,
  width,
  collapsed = false,
  onCollapseToggle,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const branding = usePharmacyBranding();
  const location = useLocation();
  const { pharmacyId } = usePharmacyStore();
  const { isNurse } = useNurseScope();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [collapsedSections, setCollapsedSections] = useState<string[]>(['SETTINGS', 'QUALITY MANAGEMENT', 'CLINIC MANAGEMENT', 'DRIVER MANAGEMENT', 'IN DEVELOPMENT']);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);

  // PRX-138: the same filter the keyboard shortcuts read (useVisibleMenuItems).
  const menuItems = useVisibleMenuItems();

  // GP-1400: live nav counts. Only queried when the item that shows them
  // survived the permission filter above — no polling for a badge nobody sees.
  const showsUnreadBadge = React.useMemo(
    () =>
      menuItems.some(
        item =>
          item.badgeKey === 'unreadConversations' ||
          item.subItems?.some(sub => sub.badgeKey === 'unreadConversations'),
      ),
    [menuItems],
  );
  const unreadConversations = useUnreadConversationBadge(showsUnreadBadge);
  // PRX-67: same "only poll while visible" gating as the unread badge above —
  // the Task Queue count query is keyed off the active pharmacy, not a role.
  const showsMyOpenTasksBadge = React.useMemo(
    () =>
      menuItems.some(
        item =>
          item.badgeKey === 'myOpenTasks' ||
          item.subItems?.some(sub => sub.badgeKey === 'myOpenTasks'),
      ),
    [menuItems],
  );
  const myOpenTasks = useTaskQueueCounts(
    showsMyOpenTasksBadge ? pharmacyId ?? undefined : undefined,
  ).data?.mine;
  const dynamicBadges: Record<NavBadgeKey, number | undefined> = {
    unreadConversations,
    myOpenTasks,
  };
  /** Live count when the item declares a `badgeKey`, else its static badge. */
  const badgeFor = (item: MenuItem | SubMenuItem): number | 'new' | 'IP' | undefined =>
    item.badgeKey ? dynamicBadges[item.badgeKey] : item.badge;

  // Auto-expand active menu on mount
  React.useEffect(() => {
    const currentPath = location.pathname;
    const activeMenu = menuItems.find(item =>
      item.subItems?.some(subItem => subItem.path === currentPath)
    );
    if (activeMenu) {
      setExpandedMenus([activeMenu.text]);
    }
  }, [location.pathname, menuItems]);

  const toggleSubmenu = (menuText: string) => {
    // Accordion behavior - only one menu open at a time
    setExpandedMenus(expandedMenus.includes(menuText) ? [] : [menuText]);
  };

  const toggleSection = (sectionName: string) => {
    setCollapsedSections(prev =>
      prev.includes(sectionName)
        ? prev.filter(s => s !== sectionName)
        : [...prev, sectionName]
    );
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  const renderBadge = (badge: number | 'new' |  'IP' | undefined, badgeKey?: NavBadgeKey) => {
    // Falsy covers both "no badge" and a zero count — a 0 is not news (GP-1400),
    // and `undefined` (count not loaded yet) must not paint a placeholder.
    if (!badge) return null;

    if (badge === 'new' ) {
      return (
        <Box
          sx={{
            bgcolor: theme.palette.error.main,
            color: 'white',
            fontSize: 9,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          New
        </Box>
      );
    } else if (badge === 'IP') {
      return (
        <Box
          sx={{
            bgcolor: theme.palette.info.main,
            color: theme.palette.info.contrastText,
            fontSize: 8,
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          WIP
        </Box>
      );
    }

    // Numeric counts render as a solid pill on the row (a childless MUI Badge
    // has nothing to anchor to). Same look as the unread chip on the Scheduling
    // queue's Communication tab.
    return (
      <Chip
        label={badge}
        color="error"
        size="small"
        data-testid={badgeKey ? `nav-badge-${badgeKey}` : undefined}
        aria-label={badgeKey ? `${badge} ${NAV_BADGE_LABELS[badgeKey]}` : undefined}
        sx={{
          // GP-1441: the pill is a flex child of the nav row and MUI's Chip
          // shrinks like any other (`flex-shrink: 1`) while `.MuiChip-label`
          // ellipsizes what no longer fits. On "Communication Center" — the one
          // label long enough to wrap onto two lines — the row ran out of width
          // and an unread count of 10 rendered as "1…", a red badge quietly
          // lying about how much is waiting. The count is the payload here, so
          // it never gives up space; ListItemText (`flex: 1 1 auto; min-width: 0`)
          // absorbs the whole squeeze by wrapping, which keeps the pill at its
          // natural width for 2- and 3-digit values alike.
          flexShrink: 0,
          // Now that the label expands right up to the pill, keep them apart
          // (same spacing as ShortcutHint).
          ml: 1,
          height: 20,
          minWidth: 20,
          // Belt to flexShrink's braces: MUI clamps a Chip to `max-width: 100%`
          // and ellipsizes its own label. Neither can bite once the pill stops
          // shrinking, but leaving them in place means any future rule that
          // squeezes this row would silently resume lying about the count. An
          // overflowing number is recoverable; a plausible wrong one is not.
          maxWidth: 'none',
          '& .MuiChip-label': {
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'nowrap',
          },
          px: 0.5,
          fontWeight: 700,
        }}
      />
    );
  };

  const drawerContent = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flex: 'initial',
            '&:hover': {
              opacity: 0.8,
            },
          }}
          onClick={() => handleNavigation(isNurse ? NURSE_LANDING_PATH : '/order-fulfillment/operations-dashboard')}
        >
          <img
            src={branding.logo}
            alt={BRAND.name}
            style={{ 
              height: '36px',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
            }} 
          />
        </Box>
        
        {/* Collapse button - only show on desktop */}
        {variant === 'permanent' && onCollapseToggle && (
          <IconButton
            onClick={onCollapseToggle}
            size="small"
            sx={{
              position: collapsed ? 'absolute' : 'relative',
              right: collapsed ? '50%' : 0,
              transform: collapsed ? 'translateX(50%)' : 'none',
              bgcolor: theme.palette.primary.main,
              color: 'white',
              width: 28,
              height: 28,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Menu Items */}
      <List sx={{ 
        flex: 1, 
        px: 1, 
        py: 2, 
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.primary.main, 0.2),
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: alpha(theme.palette.primary.main, 0.3),
        },
      }}>
        {menuItems.map((item, index) => {
          // Track if we're in a collapsed section
          let currentSection: string | null = null;
          let isInCollapsedSection = false;
          
          // Find the current section
          for (let i = index - 1; i >= 0; i--) {
            if (menuItems[i].isSection) {
              currentSection = menuItems[i].text;
              isInCollapsedSection = menuItems[i].isCollapsibleSection ? 
                collapsedSections.includes(currentSection) : false;
              break;
            }
          }
          
          // Handle section headers
          if (item.isSection) {
            return !collapsed ? (
              <Box
                key={`section-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  mt: index > 0 ? 1 : 0,
                  mb: 0.5,
                  cursor: item.isCollapsibleSection ? 'pointer' : 'default',
                  '&:hover': item.isCollapsibleSection ? {
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 1,
                  } : {},
                }}
                onClick={() => item.isCollapsibleSection && toggleSection(item.text)}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {item.text}
                </Typography>
                {item.isCollapsibleSection && (
                  collapsedSections.includes(item.text) ? 
                    <ExpandMore fontSize="small" sx={{ color: theme.palette.text.secondary }} /> : 
                    <ExpandLess fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                )}
              </Box>
            ) : null;
          }
          
          // Handle dividers
          if (item.isDivider) {
            // Hide divider if it's in a collapsed section
            if (isInCollapsedSection) return null;
            return (
              <Divider 
                key={`divider-${index}`}
                sx={{ 
                  my: 2, 
                  mx: collapsed ? 1 : 2,
                  borderColor: alpha(theme.palette.divider, 0.4),
                }} 
              />
            );
          }
          
          // Hide menu items if they're in a collapsed section
          if (isInCollapsedSection) return null;
          
          // `matchNestedPaths` keeps the parent highlighted on child routes, so
          // the nav reflects where you are on /reports/* too (GP-1327).
          const isActive =
            item.path === location.pathname ||
            (!!item.matchNestedPaths && !!item.path && location.pathname.startsWith(`${item.path}/`));
          const isSubItemActive = item.subItems?.some(sub => sub.path === location.pathname);
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const isExpanded = expandedMenus.includes(item.text);
          // GP-482: the queue's hotkey, straight from the shared registry.
          const itemShortcut = item.path ? shortcutForPath(item.path) : undefined;
          const itemBadge = badgeFor(item);
          // Collapsed rail has no room for a label, let alone a pill — pin the
          // count to the icon instead so it survives Ctrl+B (GP-1400).
          const railCount = collapsed && typeof itemBadge === 'number' ? itemBadge : 0;

          return (
            <React.Fragment key={item.text}>
              <Tooltip
                title={
                  collapsed
                    ? [
                        item.text,
                        railCount > 0 && item.badgeKey
                          ? `${railCount} ${NAV_BADGE_LABELS[item.badgeKey]}`
                          : '',
                        itemShortcut,
                      ]
                        .filter(Boolean)
                        .join(' — ')
                    : ''
                }
                placement="right"
                disableHoverListener={!collapsed}
              >
                <ListItem
                  disablePadding
                  sx={{ 
                    mb: 0.5,
                    ...(item.journeyColor && !collapsed && {
                      backgroundColor: alpha(item.journeyColor, 0.3),
                      borderRadius: 1,
                      mx: 0.5,
                      mb: 1,
                    }),
                  }}
                >
                  <ListItemButton 
                  onClick={() => {
                    if (hasSubItems) {
                      toggleSubmenu(item.text);
                    } else if (item.path) {
                      handleNavigation(item.path);
                    }
                  }}
                  sx={{
                    borderRadius: 1.5,
                    mx: 0.5,
                    minHeight: 44,
                    px: collapsed ? 1 : 2,
                    justifyContent: collapsed ? 'center' : 'initial',
                    '&:hover': {
                      bgcolor: item.journeyColor 
                        ? alpha(item.journeyColor, 0.5) 
                        : alpha(theme.palette.primary.main, 0.08),
                    },
                    ...((isActive || isSubItemActive) && {
                      bgcolor: item.journeyColor 
                        ? alpha(item.journeyColor, 0.6) 
                        : alpha(theme.palette.primary.main, 0.12),
                      color: theme.palette.primary.main,
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                      },
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 40,
                      mr: collapsed ? 0 : 2,
                      justifyContent: 'center',
                    }}
                  >
                    {railCount > 0 ? (
                      <Badge
                        badgeContent={railCount}
                        color="error"
                        data-testid={item.badgeKey ? `nav-badge-${item.badgeKey}` : undefined}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: 10,
                            height: 16,
                            minWidth: 16,
                          },
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontWeight: isActive || isSubItemActive ? 600 : 400,
                          fontSize: '0.875rem',
                          // Wrap long labels ("Operations Command Center",
                          // "Subscription Management") to a second line instead of
                          // clipping them with an ellipsis (GP-1196).
                          sx: {
                            whiteSpace: 'normal',
                            overflowWrap: 'anywhere',
                            lineHeight: 1.2,
                          }
                        }}
                      />
                      {hasSubItems && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {renderBadge(itemBadge, item.badgeKey)}
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                      )}
                      {!hasSubItems && renderBadge(itemBadge, item.badgeKey)}
                      {!hasSubItems && itemShortcut && <ShortcutHint combo={itemShortcut} />}
                    </>
                  )}
                  </ListItemButton>
                </ListItem>
              </Tooltip>
              
              {hasSubItems && !collapsed && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems!.map((subItem) => {
                      const isSubActive = location.pathname === subItem.path;
                      return (
                        <ListItem key={subItem.text} disablePadding sx={{ mb: 0.3 }}>
                          <ListItemButton
                            onClick={() => handleNavigation(subItem.path!)}
                            sx={{
                              borderRadius: 1,
                              mx: 0.5,
                              pl: 4,
                              minHeight: 36,
                              '&:hover': {
                                bgcolor: item.journeyColor
                                  ? alpha(item.journeyColor, 0.4)
                                  : alpha(theme.palette.primary.main, 0.08),
                              },
                              ...(isSubActive && {
                                bgcolor: item.journeyColor
                                  ? alpha(item.journeyColor, 0.5)
                                  : alpha(theme.palette.primary.main, 0.12),
                                color: theme.palette.primary.main,
                                '& .MuiListItemIcon-root': {
                                  color: theme.palette.primary.main,
                                },
                              }),
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.text}
                              primaryTypographyProps={{
                                fontWeight: isSubActive ? 600 : 400,
                                fontSize: '0.8125rem',
                                // Wrap rather than clip so sub-item labels stay
                                // fully readable (GP-1196).
                                sx: {
                                  whiteSpace: 'normal',
                                  overflowWrap: 'anywhere',
                                  lineHeight: 1.2,
                                }
                              }}
                            />
                            {renderBadge(badgeFor(subItem), subItem.badgeKey)}
                            {subItem.path && shortcutForPath(subItem.path) && (
                              <ShortcutHint combo={shortcutForPath(subItem.path)!} />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {/* The redundant "Chat with Support" button was removed here (GP-1196):
            the avatar menu already exposes a single Chat with Support control
            (PRX-86: under Help Center), so a second one in the sidebar footer
            was a duplicate. Help stays reachable — via the Help Center button
            below (and, when collapsed, on its own). */}
        {collapsed ? (
          <Tooltip title="Help Center" arrow>
            <IconButton
              size="small"
              onClick={() => navigate('/help')}
              sx={{
                color: 'info.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.08),
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s',
              }}
            >
              <MenuBook fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            <Tooltip title="View Release Notes" arrow>
              <Typography
                variant="caption"
                onClick={() => setReleaseNotesOpen(true)}
                sx={{
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
              >
                V2.3.558
              </Typography>
            </Tooltip>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Report a Bug" arrow>
                <IconButton 
                  size="small"
                  onClick={() => {
                    // Create a Sentry event and show the feedback dialog
                    const eventId = Sentry.captureMessage('User initiated bug report', 'info');
                    Sentry.showReportDialog({
                      eventId: eventId,
                      user: {
                        email: '',
                        name: ''
                      }
                    });
                  }}
                  sx={{ 
                    color: 'warning.main',
                    '&:hover': { 
                      backgroundColor: alpha(theme.palette.warning.main, 0.08),
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <BugReport fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Help Center" arrow>
                <IconButton
                  size="small"
                  onClick={() => navigate('/help')}
                  sx={{
                    color: 'info.main',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  <MenuBook fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>

    </Box>
  );

  if (!open && variant === 'permanent') {
    return null;
  }

  return (
    <>
      <Drawer
        variant={variant}
        open={open}
        onClose={onClose}
        sx={{
          width: collapsed ? 64 : width,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: collapsed ? 64 : width,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.divider}`,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            ...(variant === 'temporary' && {
              boxShadow: theme.shadows[16],
            }),
          },
        }}
        ModalProps={{
          keepMounted: true, // Better for mobile performance
        }}
      >
        {drawerContent}
      </Drawer>
      <ReleaseNotesModal
        open={releaseNotesOpen}
        onClose={() => setReleaseNotesOpen(false)}
      />
    </>
  );
};
